import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, useParams } from "react-router";
import { FiHash, FiLock, FiMessageSquare, FiSend, FiX, FiLoader, FiSmile, FiCornerUpLeft, FiTrash2 } from "react-icons/fi";
import { useSocket } from "../context/SocketContext";
import { useAuth } from "../context/AuthContext";
import { useCourse } from "../context/CourseContext";
import { useGroupSearch } from "../context/GroupSearchContext";
import "./CommunityChannel.css";
import ChannelEmojiPicker from "./ChannelEmojiPicker";

const names = { announcements: "Announcements" };
const dayKey = value => new Date(value).toDateString();
const dayLabel = value => dayKey(value) === new Date().toDateString() ? "Today" : new Date(value).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
function Message({ message, onThread, selected, onQuote, mutate, canPost, userId }) {
  const [picker, setPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const deleted = message.deleteStatus === "deleted";
  const author = message.sender?.username || "Deleted user";
  const initials = author.trim().split(/\\s+/).slice(0, 2).map(word => Array.from(word)[0]).join("").toUpperCase();
  const tint = Array.from(author).reduce((sum, letter) => sum + letter.codePointAt(0), 0) % 3;
  const own = String(message.sender?._id || message.sender) === String(userId);
  const act = async (action, payload = {}) => {
    if (busy) return;
    setBusy(true); setError("");
    try { await mutate(action, { messageId: message._id, ...payload }); setDeleting(false); }
    catch (error) { setError(error.message); }
    finally { setBusy(false); }
  };
  const react = emoji => act("react", { emoji, active: !message.reactions?.find(item => item.emoji === emoji)?.mine });
  return <article className="channel-message">
    <span className={`channel-avatar tint-${tint}`} aria-hidden="true">{initials}</span>
    <div className="channel-message-body min-w-0 flex-1">
      <div className="flex flex-wrap items-baseline gap-2"><h3 className="text-base font-semibold">{author}</h3><time className="text-xs text-gray-500" dateTime={message.createdAt} title={new Date(message.createdAt).toLocaleString()}>{new Date(message.createdAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</time>
      {!deleted && mutate && <div className="channel-message-actions" aria-label="Message actions">
        <button type="button" disabled={busy} title="Add reaction" aria-label="Add reaction" onClick={() => setPicker(true)}><FiSmile /></button>
        {onThread && <button type="button" title="Open thread" aria-label="Open thread" onClick={() => onThread(message._id)}><FiMessageSquare /></button>}
        {canPost && onQuote && <button type="button" title="Quote in reply" aria-label="Quote in reply" onClick={() => onQuote(message)}><FiCornerUpLeft /></button>}
        {own && <button type="button" disabled={busy} title="Delete message" aria-label="Delete message" onClick={() => setDeleting(true)}><FiTrash2 /></button>}
      </div>}</div>
      <div className="channel-message-text">
        {!deleted && message.quote && <blockquote className="channel-quote"><strong>{message.quote.author}</strong><span>{message.quote.content}</span></blockquote>}
        {deleted ? <span className="text-sm italic text-gray-500">Message deleted</span> : message.contentPreview}
      </div>
      {!deleted && message.reactions?.length > 0 && <div className="channel-reactions">{message.reactions.map(item => <button type="button" key={item.emoji} disabled={busy || !mutate} aria-pressed={item.mine} aria-label={`${item.emoji} reaction, ${item.count}`} onClick={() => react(item.emoji)}>{item.emoji} {item.count}</button>)}</div>}
      {onThread && message.replyCount > 0 && <button type="button" aria-label={message.replyCount ? `${message.replyCount} ${message.replyCount === 1 ? "reply" : "replies"}` : "Reply in thread"} aria-expanded={selected} onClick={() => onThread(message._id)} className={`channel-replies ${message.replyCount ? "has-replies" : ""}`}><span>{message.replyCount ? `${message.replyCount} ${message.replyCount === 1 ? "reply" : "replies"}` : "Reply in thread"}</span>{message.lastReplyAt && <time dateTime={message.lastReplyAt} title={new Date(message.lastReplyAt).toLocaleString()}>{new Date(message.lastReplyAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</time>}</button>}
      {deleting && <div className="channel-delete-confirm"><span>Delete this message?</span><button type="button" disabled={busy} onClick={() => act("remove")}>{busy ? "Deleting..." : "Delete"}</button><button type="button" disabled={busy} onClick={() => setDeleting(false)}>Keep</button></div>}
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      {picker && <ChannelEmojiPicker onSelect={react} onClose={() => setPicker(false)} />}
    </div>
  </article>;
}
function ChannelPane({ channel, root = null, search = "", onThread, onClose, request, ready, selectedRoot }) {
  const { userId } = useAuth();
  const { course } = useCourse();
  const client = useQueryClient();
  const [draft, setDraft] = useState("");
  const [quote, setQuote] = useState(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const input = useRef(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const pending = useRef(null);
  const bottom = useRef(null);
  const scroller = useRef(null);
  const nearBottom = useRef(true);
  const query = useInfiniteQuery({
    queryKey: ["account", userId, "community-channel", course._id, channel, root, search],
    queryFn: ({ pageParam }) => request("channel:list", { channel, root, cursor: pageParam, search }),
    initialPageParam: null, getNextPageParam: page => page.nextCursor || undefined,
    enabled: ready, refetchOnMount: "always", retry: false,
  });
  const data = query.error ? null : query.data?.pages[0];
  const messages = query.error ? [] : [...(query.data?.pages || [])].reverse().flatMap(page => page.items);
  const lastId = messages.at(-1)?._id;
  useEffect(() => {
    if (root || search || !ready || query.isFetching || query.error || !data?.readThrough) return;
    let disposed = false;
    let marked = false;
    const markRead = async () => {
      if (disposed || marked || document.visibilityState !== "visible") return;
      marked = true;
      try {
        await request("channel:read", { channel, through: data.readThrough });
        if (!disposed) client.invalidateQueries({ queryKey: ["account", userId, "channel-unread", course._id] });
      } catch { marked = false; }
    };
    markRead();
    document.addEventListener("visibilitychange", markRead);
    window.addEventListener("focus", markRead);
    return () => { disposed = true; document.removeEventListener("visibilitychange", markRead); window.removeEventListener("focus", markRead); };
  }, [root, search, ready, query.isFetching, query.error, data?.readThrough, channel, request, client, userId, course._id]);
  useEffect(() => {
    if (nearBottom.current) bottom.current?.scrollIntoView({ block: "nearest" });
  }, [lastId]);
  const send = async event => {
    event.preventDefault();
    if (sending || !draft.trim() || !ready || !data?.canPost) return;
    const content = draft.trim();
    if (pending.current?.content !== content || pending.current?.quote !== (quote?._id || null)) pending.current = { content, quote: quote?._id || null, clientId: crypto.randomUUID() };
    setSending(true); setError("");
    try {
      await request("channel:send", { channel, root, ...pending.current });
      setDraft(""); setQuote(null); pending.current = null; nearBottom.current = true;
      await client.invalidateQueries({ queryKey: ["account", userId, "community-channel", course._id, channel] });
    } catch (error) { setError(error.message); }
    finally { setSending(false); }
  };
  const mutate = async (action, payload) => {
    await request(`channel:${action}`, { channel, ...payload });
    await client.invalidateQueries({ queryKey: ["account", userId, "community-channel", course._id, channel] });
  };
  const quoteMessage = message => { setQuote(message); input.current?.focus(); };
  const insertEmoji = emoji => {
    const start = input.current?.selectionStart ?? draft.length;
    const end = input.current?.selectionEnd ?? start;
    setDraft(value => (value.slice(0, start) + emoji + value.slice(end)).slice(0, 5000));
    requestAnimationFrame(() => { input.current?.focus(); input.current?.setSelectionRange(start + emoji.length, start + emoji.length); });
  };
  const older = async () => {
    const node = scroller.current;
    const height = node.scrollHeight, top = node.scrollTop;
    await query.fetchNextPage();
    requestAnimationFrame(() => { if (scroller.current === node) node.scrollTop = top + node.scrollHeight - height; });
  };
  return <section className={`channel-pane ${root ? "channel-thread" : ""}`} aria-label={root ? "Thread" : names[channel]}>
    <header className="channel-pane-heading">
      <div className="flex min-w-0 items-center gap-3">{!root && <span className="channel-heading-icon"><FiHash /></span>}<div className="min-w-0"><h2 className="break-words text-base font-semibold">{root ? "Thread" : channel}</h2>{root && <p className="text-xs text-gray-500">{`# ${channel}`}</p>}</div></div>
      {onClose && <button type="button" onClick={onClose} aria-label="Close thread" title="Close thread" className="flex h-9 w-9 items-center justify-center rounded-md hover:bg-gray-100"><FiX /></button>}
    </header>
    <div ref={scroller} className="channel-history" onScroll={event => { const el = event.currentTarget; nearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 100; }}>
      {query.hasNextPage && <button type="button" disabled={query.isFetchingNextPage} onClick={older} className="mx-auto mb-4 block min-h-9 rounded-md border border-gray-300 px-3 text-sm">{query.isFetchingNextPage ? "Loading..." : "Older messages"}</button>}
      {root && data?.root && <div className="channel-thread-parent"><Message message={data.root} mutate={mutate} userId={userId} /></div>}
      {root && data && <div className="channel-reply-divider"><span>{messages.length}{query.hasNextPage ? "+" : ""} {messages.length === 1 && !query.hasNextPage ? "reply" : "replies"}</span></div>}
      {query.isPending && <div role="status" className="flex justify-center p-8"><FiLoader className="animate-spin" /><span className="sr-only">Loading messages</span></div>}
      {query.error && <p role="alert" className="p-4 text-sm text-red-700">{query.error.message} <button type="button" onClick={() => query.refetch()} className="underline">Retry</button></p>}
      {data && !messages.length && <p className="p-6 text-center text-sm text-gray-500">{search ? "No matching messages." : root ? "No replies yet." : "No messages yet."}</p>}
      {messages.map((message, index) => <Fragment key={message._id}>
        {!root && (index === 0 || dayKey(messages[index - 1].createdAt) !== dayKey(message.createdAt)) && <div className="channel-day"><span>{dayLabel(message.createdAt)}</span></div>}
        <Message message={message} onThread={root ? null : onThread} selected={selectedRoot === message._id} onQuote={quoteMessage} mutate={mutate} canPost={data?.canPost} userId={userId} />
      </Fragment>)}
      <div ref={bottom} />
    </div>
    {!ready && <p role="status" className="px-4 py-2 text-xs text-gray-500">Connecting...</p>}
    {data && !data.canPost && <p className="channel-readonly"><FiLock aria-hidden="true" />Only the owner and admins can post here.</p>}
    {error && <p role="alert" className="px-4 py-2 text-sm text-red-700">{error}</p>}
    {data?.canPost && quote && <div className="channel-composer-quote"><div><strong>Replying to {quote.sender?.username || "Deleted user"}</strong><p>{quote.contentPreview}</p></div><button type="button" title="Cancel reply" aria-label="Cancel reply" onClick={() => setQuote(null)}><FiX /></button></div>}
    {emojiOpen && <ChannelEmojiPicker onSelect={insertEmoji} onClose={() => setEmojiOpen(false)} />}
    {data?.canPost && <form onSubmit={send} className="channel-compose">
      <button type="button" disabled={sending} className="channel-emoji-toggle" title="Insert emoji" aria-label="Insert emoji" onClick={() => setEmojiOpen(true)}><FiSmile /></button>
      <textarea ref={input} aria-label={root ? "Thread reply" : "Channel message"} placeholder={root ? "Reply to thread..." : `Message #${channel}`} rows={1} maxLength={5000} value={draft} onChange={event => setDraft(event.target.value)} disabled={sending} />
      <button type="submit" title={root ? "Send reply" : "Send message"} aria-label={root ? "Send reply" : "Send message"} disabled={!ready || sending || !draft.trim()} className="channel-send">{sending ? <FiLoader className="animate-spin" /> : <FiSend />}</button>
    </form>}
  </section>;
}
function ChannelView({ channel }) {
  const { socket, isSocketReady } = useSocket();
  const { userId } = useAuth();
  const { course } = useCourse();
  const client = useQueryClient();
  const [root, setRoot] = useState(null);
  const [search] = useGroupSearch();
  const [query, setQuery] = useState(search);
  useEffect(() => { const timer = setTimeout(() => setQuery(search.trim()), 300); return () => clearTimeout(timer); }, [search]);
  const request = useCallback((event, payload) => new Promise((resolve, reject) => {
    if (!socket?.connected) return reject(new Error("Not connected. Please try again."));
    socket.timeout(15000).emit(event, payload, (error, response) => {
      if (error) reject(new Error("Request timed out. Please retry."));
      else if (!response?.ok) {
        if (response?.code === "SECTION_PAYMENT_REQUIRED") window.dispatchEvent(new CustomEvent("community:access-denied", { detail: response }));
        reject(new Error(response?.error || "Channel request failed"));
      }
      else resolve(response.data);
    });
  }), [socket]);
  useEffect(() => {
    const refresh = event => {
      if (!event || (event.channel === channel && event.workspaceId === String(course._id))) {
        client.invalidateQueries({ queryKey: ["account", userId, "community-channel", course._id, channel] });
      }
    };
    socket?.on("channel:changed", refresh);
    socket?.on("connect", refresh);
    return () => { socket?.off("channel:changed", refresh); socket?.off("connect", refresh); };
  }, [socket, client, userId, course._id, channel]);
  return <main className={`community-channel ${root ? "has-thread" : ""}`}>
    <ChannelPane channel={channel} search={query} onThread={setRoot} selectedRoot={root} request={request} ready={isSocketReady} />
    {root && <ChannelPane key={root} channel={channel} root={root} onClose={() => setRoot(null)} request={request} ready={isSocketReady} />}
  </main>;
}
export default function CommunityChannel() {
  const { channel } = useParams();
  if (!Object.hasOwn(names, channel)) return <Navigate to="../channels/announcements" replace />;
  return <ChannelView key={channel} channel={channel} />;
}
